import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, ReplaySubject, switchMap, take, tap, catchError, EMPTY, map, forkJoin } from 'rxjs';

@Injectable({providedIn: 'root'})
export class NotificationsService
{
    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);
    private _unreadCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient)
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification[]>
    {
        return this._notifications.asObservable();
    }

    /**
     * Getter for unread count
     */
    get unreadCount$(): Observable<number>
    {
        return this._unreadCount.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Fetch notifications with filter: all, unread, read, new, deleted
     */
    getNotifications(filter: 'all' | 'unread' | 'read' | 'new' | 'deleted' = 'all'): Observable<any>
    {
        return this._httpClient.get<any>(`${environment.apiNest_2}/api/v1/notifications`, {
            params: { filter },
            headers: { 'Content-Type': 'application/json' }
        }).pipe(
            tap((response) => {
                console.log('API Response in Service:', response);
                const notificationsArray = response && Array.isArray(response.notifications) ? response.notifications : [];
                const formattedNotifications = notificationsArray.filter(n => !!n).map((n: any) => ({
                    id: n.id,
                    user_id: n.user_id,
                    type: n.type,
                    icon: 'heroicons_outline:bell',
                    image: n.sender?.avatar,
                    title: n.title || 'Notification',
                    description: n.body || n.message || '',
                    time: n.created_at || new Date(),
                    read: !!n.is_read,
                    is_read: !!n.is_read,
                    created_at: n.created_at,
                    link: n.link || n.data?.link || '',
                    redirect_url: n.redirect_url,
                    data: n.data,
                    firebase_token: n.firebase_token,
                    internal_push_id: n.internal_push_id,
                    sender: n.sender,
                    priority: n.priority,
                    read_at: n.read_at,
                    deleted_at: n.deleted_at,
                }));

                console.log('Formatted Notifications in Service:', formattedNotifications);
                this._notifications.next(formattedNotifications);
                if (filter === 'unread') {
                    this._unreadCount.next(formattedNotifications.length);
                }
            }),
            catchError((err: any) => {
                console.error('Error Fetching Notifications:', err);
                return EMPTY;
            })
        );
    }

    /**
     *  le compteur avec l'API existante
     */
    getUnreadCount(): Observable<number> {
        return this._httpClient.get<any>(`${environment.apiNest_2}/api/v1/notifications`, {
            params: { filter: 'unread' },
            headers: { 'Content-Type': 'application/json' }
        }).pipe(
            map(response => {
                const notifications = response?.notifications || [];
                return notifications.length;
            }),
            tap(count => {
                console.log('Updated unread count from API:', count);
                this._unreadCount.next(count);
            }),
            catchError((err) => {
                console.error('Error fetching unread count:', err);
                return this._unreadCount.asObservable().pipe(take(1));
            })
        );
    }

    refreshUnreadCount(): void {
        this.getUnreadCount().subscribe();
    }

    create(notification: Notification): Observable<Notification>
    {
        return this.notifications$.pipe(
            take(1),
            switchMap(notifications => this._httpClient.post<Notification>(`${environment.apiNest_2}/api/v1/notifications`, {notification}).pipe(
                tap((newNotification) =>
                {
                    const updatedNotifications = [...notifications, newNotification];
                    this._notifications.next(updatedNotifications);
                    // Rafraîchir le compteur
                    this.refreshUnreadCount();
                })
            ))
        );
    }

    /**
     * Mark notifications as read
     *
     * @param ids
     */
    markAsRead(ids: string[]): Observable<any>
    {
        return this._httpClient.patch(`${environment.apiNest_2}/api/v1/notifications/mark-as-read`, ids).pipe(
            tap(() => {
                this.notifications$.pipe(take(1)).subscribe(notifications => {
                    const updatedNotifications = notifications.map(n =>
                        ids.includes(n.id) ? { ...n, read: true, is_read: true, read_at: new Date().toISOString() } : n
                    );
                    this._notifications.next(updatedNotifications);
                });

                setTimeout(() => {
                    this.refreshUnreadCount();
                }, 100);
            }),
            catchError((err) => {
                console.error('Error Marking Notifications as Read:', err);
                return EMPTY;
            })
        );
    }

    /**
     * Mark notifications as unread
     */
    markAsUnread(ids: string[]): Observable<any>
    {
        return this._httpClient.patch(`${environment.apiNest_2}/api/v1/notifications/mark-as-unread`, ids).pipe(
            tap(() => {
                this.notifications$.pipe(take(1)).subscribe(notifications => {
                    const updatedNotifications = notifications.map(n =>
                        ids.includes(n.id) ? { ...n, read: false, is_read: false, read_at: null } : n
                    );
                    this._notifications.next(updatedNotifications);
                });
            }),
            catchError((err) => {
                console.error('Error Marking Notifications as Unread:', err);
                return EMPTY;
            })
        );
    }

    /**
     * Delete multiple notifications
     */
    deleteMany(ids: string[]): Observable<any>
    {
        return this._httpClient.request('delete', `${environment.apiNest_2}/api/v1/notifications/delete-many`, { body: ids }).pipe(
            tap(() => {
                this.notifications$.pipe(take(1)).subscribe(notifications => {
                    const updatedNotifications = notifications.filter(n => !ids.includes(n.id));
                    this._notifications.next(updatedNotifications);
                });

                setTimeout(() => {
                    this.refreshUnreadCount();
                }, 100);
            }),
            catchError((err) => {
                console.error('Error Deleting Notifications:', err);
                return EMPTY;
            })
        );
    }
}
